import { useEffect, type RefObject } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Atrapa el foco dentro de un panel temporal (drawer, hoja inferior, panel de
 * notificaciones) mientras está abierto, y lo devuelve al elemento que lo abrió
 * al cerrarse. Sin esto el Tab se escapa al contenido de detrás, que en móvil
 * queda tapado por el overlay: el lector de pantalla lee una pantalla que el
 * usuario no ve.
 *
 * Uso: `useFocusTrap(panelRef, isOpen)`.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement>,
  isActive: boolean,
) {
  useEffect(() => {
    if (!isActive) return;
    const panel = containerRef.current;
    if (!panel) return;

    const opener = document.activeElement as HTMLElement | null;
    const getFocusable = () =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement,
      );

    // El contenido puede llegar después (panel lazy tras un Suspense): si al
    // abrir no hay nada enfocable, el foco se queda en el propio panel y pasa
    // al primer control en cuanto aparece, salvo que el usuario ya lo movió.
    // `data-autofocus` marca el control que debe recibir el foco al abrir (p.
    // ej. "Cerrar"), aunque no sea el primero del DOM.
    const focusFirst = () => {
      const focusable = getFocusable();
      const first =
        focusable.find((element) => element.hasAttribute('data-autofocus')) ?? focusable[0];
      if (first) {
        first.focus();
        return true;
      }
      return false;
    };

    let observer: MutationObserver | null = null;
    if (!focusFirst()) {
      if (panel.hasAttribute('tabindex')) panel.focus();
      observer = new MutationObserver(() => {
        const active = document.activeElement;
        const focusStillPending =
          active === panel || active === opener || active === document.body || active === null;
        if (!focusStillPending || focusFirst()) {
          observer?.disconnect();
          observer = null;
        }
      });
      observer.observe(panel, { childList: true, subtree: true });
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        // Contenido aún cargando: sin esto el Tab saltaba a la página de detrás.
        event.preventDefault();
        if (panel.hasAttribute('tabindex')) panel.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const isOutside = !(active instanceof Node) || !panel.contains(active);

      if (event.shiftKey && (isOutside || active === first)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (isOutside || active === last)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      observer?.disconnect();
      document.removeEventListener('keydown', handleKeyDown, true);
      opener?.focus?.();
    };
  }, [containerRef, isActive]);
}
